/*
  Composes the support message. Pure by design: the clock, the route, and the identity of
  the customer arrive as a FeedbackContext value, so every branch here is testable without a
  browser. There is no feedback endpoint in either OpenAPI document, so a mailto: draft the
  user sends from their own client is the delivery mechanism.
*/

import { FeedbackDraft } from './feedbackDraft'

/*
  The one place the support address lives. Support.vue reads it from here too, so the two
  cannot drift apart. It is a constant on purpose — no code path may take a recipient from
  user input, or a crafted summary could redirect the message.
*/
export const SUPPORT_EMAIL = 'ims-support@shoplogix.com'

export type FeedbackContext = {
  pageLabel: string
  pageUrl: string
  sentAt: string
  browser: string
  customerName?: string
}

const LINE_BREAK = '\r\n'

/* Mail bodies are CRLF-delimited, including the line breaks the user typed in the textarea. */
function toCrlf(value: string): string {
  return value.replace(/\r\n|\r|\n/g, LINE_BREAK)
}

/*
  encodeURIComponent throws 'URI malformed' on a lone surrogate: half of a code point whose
  other half was cut away upstream. clampWithNotice no longer produces one, but this module
  is the last step before a URI the whole 'Prepare email' action depends on, and a throw here
  leaves that action doing nothing visible at all. Replacing an orphan with U+FFFD keeps
  every function below total — a message carrying one replacement glyph is still deliverable,
  where an exception is not.

  The first branch matches a well-formed pair and hands it back untouched, so only a
  surrogate with no partner reaches the replacement. A lookbehind would say the same thing
  in less space, but Safari gained lookbehind only in 16.4 and this code ships to browsers.
*/
const REPLACEMENT_CHARACTER = String.fromCharCode(0xfffd)

function replaceLoneSurrogates(value: string): string {
  return value.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]/g, (match) =>
    match.length === 2 ? match : REPLACEMENT_CHARACTER
  )
}

/*
  encodeURIComponent leaves a literal '+' alone, and a mailto query is read as
  application/x-www-form-urlencoded by enough clients that the '+' would arrive as a space.
  Encoding it keeps the text the user typed intact.
*/
function encodeMailComponent(value: string): string {
  return encodeURIComponent(replaceLoneSurrogates(value)).replace(/\+/g, '%2B')
}

export function buildSubject(draft: FeedbackDraft): string {
  const summary = draft.summary.trim()

  /*
    The interface blocks an empty summary, but the function stays total: a partial subject is
    still a deliverable message, where a thrown error is not.
  */
  return summary ? `[${draft.requestType}] ${summary}` : `[${draft.requestType}]`
}

export function buildBody(draft: FeedbackDraft, context: FeedbackContext): string {
  const site = context.customerName?.trim()

  const lines = [
    toCrlf(draft.details.trim()),
    '',
    '---',
    `Page: ${context.pageLabel}`,
    `URL: ${context.pageUrl}`
  ]

  /*
    A super admin has no customer, so the line is left out rather than sent empty. An empty
    'Site:' reads as a data fault to whoever triages the mailbox.
  */
  if (site) {
    lines.push(`Site: ${site}`)
  }

  lines.push(`Sent: ${context.sentAt}`, `Browser: ${context.browser}`)

  return lines.join(LINE_BREAK)
}

export function mailtoHref(draft: FeedbackDraft, context: FeedbackContext): string {
  const subject = encodeMailComponent(buildSubject(draft))
  const body = encodeMailComponent(buildBody(draft, context))

  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
}

/*
  What to call the page in the message. A named route reads better than a path to the person
  triaging it, but not every route carries a name.
*/
export function pageLabel(route: { name?: unknown; path?: string }): string {
  if (typeof route.name === 'string' && route.name.length > 0) {
    return route.name
  }

  return route.path || '/'
}
